import Agilite from 'agilite'
import store from '../../../../store'
import BillingEnums from '../../utils/enums'

const agilite = new Agilite({
  apiServerUrl: process.env.REACT_APP_AGILITE_API_URL,
  apiKey: process.env.REACT_APP_AGILITE_API_KEY
})

const { profileKeys, routeKeys } = BillingEnums

/**
 * Create an audit trail entry
 */
export const createAuditEntry = async (entryData) => {
  try {
    const auditEntry = {
      ...entryData,
      entityRef: store.getState().core.entity._id,
      timestamp: new Date(),
      ipAddress: window.location.hostname, // In a real app, get from server
      userAgent: navigator.userAgent,
      createdAt: new Date()
    }

    return await agilite.Connectors.execute(profileKeys.AUDIT_TRAIL, routeKeys.CREATE, {
      data: JSON.stringify(auditEntry)
    })
  } catch (error) {
    console.error('Error creating audit entry:', error)
    throw error
  }
}

/**
 * Get audit trail entries with pagination and filters
 */
export const getAuditTrail = async (filters = {}, page = 1, limit = 20) => {
  try {
    const query = {
      entityRef: store.getState().core.entity._id
    }

    // Add filters
    if (filters.accountId) {
      // Use billingId as the primary filter (this is what we standardized on)
      query['metadata.billingId'] = filters.accountId
    }

    // Handle direct metadata filters (like metadata.billingId, metadata.patientId, etc.)
    Object.keys(filters).forEach((key) => {
      if (key.startsWith('metadata.')) {
        query[key] = filters[key]
      }
    })

    if (filters.entityType) {
      query.entityType = filters.entityType
    }
    if (filters.entityId) {
      query.entityId = filters.entityId
    }
    if (filters.action) {
      query.action = filters.action
    }
    if (filters.userId) {
      query.userId = filters.userId
    }
    if (filters.startDate && filters.endDate) {
      query.timestamp = {
        $gte: new Date(filters.startDate),
        $lte: new Date(filters.endDate)
      }
    }
    if (filters.search) {
      query.$or = [
        { userName: { $regex: filters.search, $options: 'i' } },
        { description: { $regex: filters.search, $options: 'i' } }
      ]
    }

    console.log('🔍 getAuditTrail query:', JSON.stringify(query, null, 2))
    console.log('📊 Filters received:', filters)

    const [dataResponse, countResponse] = await Promise.all([
      agilite.Connectors.execute(profileKeys.AUDIT_TRAIL, routeKeys.READ, {
        filter: JSON.stringify(query),
        page,
        pageLimit: limit,
        sort: JSON.stringify({ timestamp: -1 })
      }),
      agilite.Connectors.execute(profileKeys.AUDIT_TRAIL, routeKeys.COUNT, {
        filter: JSON.stringify(query)
      })
    ])

    console.log('📦 getAuditTrail response:', {
      count: dataResponse.data?.length || 0,
      total: countResponse.data || 0,
      sample: dataResponse.data?.slice(0, 3).map((entry) => ({
        action: entry.action,
        entityType: entry.entityType,
        description: entry.description,
        metadata: entry.metadata
      }))
    })

    return {
      data: dataResponse.data || [],
      total: countResponse.data || 0
    }
  } catch (error) {
    console.error('Error fetching audit trail:', error)
    throw error
  }
}

/**
 * Get detailed audit entry
 */
export const getAuditDetails = async (auditId) => {
  try {
    return await agilite.Connectors.execute(profileKeys.AUDIT_TRAIL, routeKeys.FIND_BY_ID, {
      id: auditId
    })
  } catch (error) {
    console.error('Error fetching audit details:', error)
    throw error
  }
}

/**
 * Audit helper function to track changes
 */
export const trackChanges = (oldData, newData, fieldsToTrack) => {
  const changes = []

  fieldsToTrack.forEach((field) => {
    if (oldData[field] !== newData[field]) {
      changes.push({
        field,
        oldValue: oldData[field],
        newValue: newData[field]
      })
    }
  })

  return changes
}

/**
 * Audit decorator for API functions
 */
export const withAuditTrail = (action, entityType) => {
  return (originalFunction) => {
    return async (...args) => {
      const startTime = Date.now()
      let result
      let error

      try {
        result = await originalFunction(...args)
      } catch (err) {
        error = err
      }

      // Create audit entry
      try {
        const user = store.getState().auth.agiliteUser
        const auditEntry = {
          action,
          entityType,
          entityId: result?._id || args[0], // Use result ID or first argument
          userId: user?._id || 'system',
          userName: user ? `${user.firstName} ${user.lastName}` : 'System',
          description: `${action} ${entityType}`,
          duration: Date.now() - startTime,
          success: !error,
          errorMessage: error?.message,
          metadata: {
            args: args.slice(0, 2), // Only log first 2 args to avoid sensitive data
            result: result?._id,
            // Note: The withAuditTrail decorator is generic and doesn't know about billingId
            // Use logAuditEvent instead for billing-specific operations that need billingId
            note: 'For billing operations, use logAuditEvent() to include billingId in metadata'
          }
        }

        await createAuditEntry(auditEntry)
      } catch (auditError) {
        console.error('Failed to create audit entry:', auditError)
        // Don't throw - audit failure shouldn't break the operation
      }

      if (error) throw error
      return result
    }
  }
}

/**
 * Simplified audit helper for common use cases
 */
export const logAuditEvent = async (action, entityType, entityId, description, metadata = {}) => {
  try {
    const user = store.getState().auth.agiliteUser

    await createAuditEntry({
      action,
      entityType,
      entityId,
      userId: user?._id || 'system',
      userName: user ? `${user.firstName} ${user.lastName}` : 'System',
      description,
      metadata
    })
  } catch (error) {
    console.error('Audit logging failed:', error)
    // Don't throw - audit failure shouldn't break the operation
  }
}

/**
 * Delete all audit trail entries (for testing purposes)
 * @returns {Promise}
 */
export const deleteAllAuditTrail = () => {
  return new Promise((resolve, reject) => {
    ;(async () => {
      try {
        // Delete ALL audit trail entries without any filter
        const filter = {}

        const response = await agilite.Connectors.execute(profileKeys.AUDIT_TRAIL, routeKeys.DELETE_MANY, {
          filter: JSON.stringify(filter)
        })
        resolve(response.data)
      } catch (e) {
        reject(e)
      }
    })()
  })
}

// Export audit actions enum
export const AuditActions = {
  CREATE: 'CREATE',
  UPDATE: 'UPDATE',
  DELETE: 'DELETE',
  APPROVE: 'APPROVE',
  REJECT: 'REJECT',
  PAYMENT: 'PAYMENT',
  REFUND: 'REFUND',
  WRITE_OFF: 'WRITE_OFF',
  ALLOCATE: 'ALLOCATE',
  VOID: 'VOID',
  EXPORT: 'EXPORT',
  IMPORT: 'IMPORT',
  LOGIN: 'LOGIN',
  LOGOUT: 'LOGOUT'
}

// Export entity types enum
export const AuditEntityTypes = {
  INVOICE: 'INVOICE',
  PAYMENT: 'PAYMENT',
  CREDIT_NOTE: 'CREDIT_NOTE',
  REFUND: 'REFUND',
  FOLLOW_UP_ACCOUNT: 'FOLLOW_UP_ACCOUNT',
  CLAIM: 'CLAIM',
  PATIENT_LEDGER: 'PATIENT_LEDGER',
  ALLOCATION: 'ALLOCATION',
  USER: 'USER',
  SETTINGS: 'SETTINGS'
}
